import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { resources } from "@constants/hardcoded-strings";
import { VERIFICATION_TRANSACTION_DETAILS_REFERENCE_DATA_TYPES, VerificationTransactionDetailsReferenceData } from "@constants/reference-data";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import { VerificationTransactionScoreTypes } from "@enums/verification-transaction-score-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { ApplicationState } from "@idw-types/state/application-state";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getVerificationTransactionDetailsDataReduxRequest } from "@redux-requests/verification-transaction-details";
import { navigateToUtil } from "@utils/navigation-util";
import {
    downloadVerificationTransactionDetailsClaimsAssertionsFile,
    downloadVerificationTransactionDetailsPolicyDecisionsFile,
    downloadVerificationTransactionDetailsScoresFile,
    downloadVerificationTransactionDetailsTransactionEventsFile
} from "@utils/file-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { PolicyDecisionsTableContainer } from "./policy-decisions-table-container/policy-decisions-table-container";
import { ClaimAssertionsTableContainer } from "./claim-assertions-table-container/claim-assertions-table-container";
import { TransactionEventsTableContainer } from "./transaction-events-table-container/transaction-events-table-container";
import { ScoresTableContainer } from "./scores-table-container/scores-table-container";
import { VerificationTransactionDetailsSummaryContainer } from "./verification-tranasaction-details-summary-container/verification-transaction-details-summary-container";
import { VerificationTransactionDetailsComponent } from "./verification-transaction-details-component/verification-transaction-details-component";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import styles from "./verification-transaction-details-container.less";


export const VerificationTransactionDetailsContainer = () => {
    const { transactionId } = useParams<{ transactionId: string; }>();
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();
    const {
        verificationTransactionDetailsData
    } = useSelector((state: ApplicationState) => ({
        verificationTransactionDetailsData: state.verificationTransactionDetails.verificationTransactionDetailsData
    }));

    const referenceData = useReferenceDataSelector<VerificationTransactionDetailsReferenceData>(VERIFICATION_TRANSACTION_DETAILS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadVerificationTransactionDetails, setShouldLoadVerificationTransactionDetails] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.verificationTransactionDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                VERIFICATION_TRANSACTION_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.REPORTS,
                CommonReferenceDataPageTypes.VERIFICATION_TRANSACTION_DETAILS
            );
        }
    }, [referenceData.isLoading.verificationTransactionDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            // referenceData.apAssertions &&
            referenceData.axnAssertions &&
            referenceData.axnAttributeTypes &&
            // referenceData.axnCompanies &&
            // referenceData.axnIdentityProviders &&
            // referenceData.axnRPServicesWithSOIDs &&
            referenceData.axnRelyingParties &&
            // referenceData.axnSelectedServiceOfferingCompanies &&
            referenceData.axnSelectedServiceOfferingSOs &&
            referenceData.axnTransactionTypes &&
            // referenceData.claimStatuses &&
            // referenceData.policyDecisions &&
            // referenceData.simpleAXNWorkflows
            referenceData.axnCountries &&
            referenceData.documentationLinks
        );
    }, [
        // referenceData.apAssertions,
        referenceData.axnAssertions,
        referenceData.axnAttributeTypes,
        // referenceData.axnCompanies,
        // referenceData.axnIdentityProviders,
        // referenceData.axnRPServicesWithSOIDs,
        referenceData.axnRelyingParties,
        // referenceData.axnSelectedServiceOfferingCompanies,
        referenceData.axnSelectedServiceOfferingSOs,
        referenceData.axnTransactionTypes,
        // referenceData.claimStatuses,
        // referenceData.policyDecisions,
        // referenceData.simpleAXNWorkflows
        referenceData.axnCountries,
        referenceData.documentationLinks
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !verificationTransactionDetailsData.isLoading && shouldLoadVerificationTransactionDetails) {
            setShouldLoadVerificationTransactionDetails(false);
            getVerificationTransactionDetailsDataReduxRequest(dispatch)(
                transactionId,
                referenceData.axnRelyingParties,
                referenceData.axnTransactionTypes,
                referenceData.axnSelectedServiceOfferingSOs,
                referenceData.axnAttributeTypes,
                referenceData.axnAssertions,
                referenceData.axnCountries
            );
        }
    }, [isReferenceDataPresent, verificationTransactionDetailsData.isLoading, shouldLoadVerificationTransactionDetails]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.verificationTransactionDetails || verificationTransactionDetailsData.isLoading;
    }, [referenceData.isLoading.verificationTransactionDetails, verificationTransactionDetailsData.isLoading]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            verificationTransactionDetailsData.verificationTransactionDetails &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadVerificationTransactionDetails
        );
    }, [
        isLoading,
        verificationTransactionDetailsData.verificationTransactionDetails,
        isReferenceDataPresent,
        shouldLoadReferenceData,
        shouldLoadVerificationTransactionDetails
    ]);

    const preTitleElement = useMemo(() => {
        const renderTooltip = (props: any) => (
            <Tooltip id="verification-transaction-back-arrow-tooltip" {...props}>
                {resources.common.buttons.goBack.label}
            </Tooltip>
        );

        const onClickBackArrow = () => {
            if (searchParams.has("previousPath")) {
                navigateToUtil(searchParams.get("previousPath"), navigate, routerLocation.pathname);
            } else {
                navigateToUtil(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname);
            }
        };

        return (
            <OverlayTrigger overlay={renderTooltip}>
                <button title={resources.common.buttons.goBack.label} className={styles.backArrowContainer} onClick={onClickBackArrow}>
                    <FontAwesomeIcon className={styles.backArrow} icon={faChevronLeft} />
                </button>
            </OverlayTrigger>
        );
    }, []);

    const headerButton = useMemo(() => {
        const onClickExportToPDF = () => {
            window.print();
        };

        return (
            <Button id="export-transaction-detail-to-pdf-button" disabled={isLoading || !isLoaded} onClick={onClickExportToPDF}>
                {resources.pages.verificationTransactionDetails.headerButton}
            </Button>
        );
    }, [isLoading, isLoaded]);

    const policyDecisionsTableHeaderButton = useMemo(() => {
        const onClickExportToCSV = () => {
            if (verificationTransactionDetailsData.verificationTransactionDetails?.policyDecisions) {
                downloadVerificationTransactionDetailsPolicyDecisionsFile(
                    "Transaction_Policy_Decisions_Export",
                    verificationTransactionDetailsData.verificationTransactionDetails.policyDecisions
                );
            }
        };

        return (
            <Button id="export-transaction-detail-policy-decision-to-pdf-button" onClick={onClickExportToCSV} disabled={
                !isLoaded ||
                isLoading ||
                !verificationTransactionDetailsData.verificationTransactionDetails?.policyDecisions?.length
            }>
                {resources.common.buttons.exportToCSV.label}
            </Button>
        );
    }, [isLoaded, isLoading]);

    const scoresTableHeaderButton = useMemo(() => {
        const scoresToExport = (verificationTransactionDetailsData.verificationTransactionDetails?.scores || [])
            .filter(score => score.scoreType === VerificationTransactionScoreTypes.IDW_RISK_SCORE)
            .map(score => ({
                ...score,
                scoreType: VerificationTransactionScoreTypes.IDW_TRUST_SCORE
            }));

        const onClickExportToCSV = () => {
            downloadVerificationTransactionDetailsScoresFile(
                "Transaction_Scores_Export",
                scoresToExport
            );
        };

        return (
            <Button id="export-transaction-detail-scores-to-pdf-button" onClick={onClickExportToCSV} disabled={!isLoaded || isLoading || !scoresToExport.length}>
                {resources.common.buttons.exportToCSV.label}
            </Button>
        );
    }, [isLoaded, isLoading]);

    const claimsTableHeaderButton = useMemo(() => {
        const onClickExportToCSV = () => {
            if (verificationTransactionDetailsData.verificationTransactionDetails?.claims) {
                downloadVerificationTransactionDetailsClaimsAssertionsFile(
                    "Transaction_Claims_Assertions_Export",
                    verificationTransactionDetailsData.verificationTransactionDetails.claims
                );
            }
        };

        return (
            <Button id="export-transaction-detail-claims-to-pdf-button" onClick={onClickExportToCSV} disabled={
                !isLoaded ||
                isLoading ||
                !verificationTransactionDetailsData.verificationTransactionDetails?.claims?.length
            }>
                {resources.common.buttons.exportToCSV.label}
            </Button>
        );
    }, [isLoaded, isLoading]);

    const transactionEventsTableHeaderButton = useMemo(() => {
        const onClickExportToCSV = () => {
            if (verificationTransactionDetailsData.verificationTransactionDetails?.transactionEvents) {
                downloadVerificationTransactionDetailsTransactionEventsFile(
                    "Transaction_Events_Export",
                    verificationTransactionDetailsData.verificationTransactionDetails.transactionEvents
                );
            }
        };

        return (
            <Button id="export-transaction-detail-events-to-pdf-button" onClick={onClickExportToCSV} disabled={
                !isLoaded ||
                isLoading ||
                !verificationTransactionDetailsData.verificationTransactionDetails?.transactionEvents?.length
            }>
                {resources.common.buttons.exportToCSV.label}
            </Button>
        );
    }, [isLoaded, isLoading]);

    return (
        <CardContainer className={styles.verificationTransactionDetailsContainer}>
            <ContentPageHeader
                title={resources.pages.verificationTransactionDetails.header}
                preTitleElement={preTitleElement}
                headerButton={headerButton}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"verification-transaction-details-page-header"}
                    />
                }
                className={styles.verificationTransactionDetailsContainerHeader}
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <>
                        <VerificationTransactionDetailsComponent verificationTransactionDetails={verificationTransactionDetailsData.verificationTransactionDetails} />
                        <VerificationTransactionDetailsSummaryContainer summary={verificationTransactionDetailsData.verificationTransactionDetails.summary} />
                        <CardContainer>
                            <ContentPageHeader title={resources.tables.verificationTransactionDetailsPolicyDecisions.title} headerButton={policyDecisionsTableHeaderButton} />
                            <ContentPageBody isMaxWidth={true}>
                                <PolicyDecisionsTableContainer policyDecisions={verificationTransactionDetailsData.verificationTransactionDetails.policyDecisions} />
                            </ContentPageBody>
                        </CardContainer>
                        <CardContainer>
                            <ContentPageHeader title={resources.tables.verificationTransactionDetailsScores.title} headerButton={scoresTableHeaderButton} />
                            <ContentPageBody isMaxWidth={true}>
                                <ScoresTableContainer
                                    scores={verificationTransactionDetailsData.verificationTransactionDetails.scores}
                                    trustScoreBreakdowns={verificationTransactionDetailsData.verificationTransactionDetails.trustScoreBreakdowns}
                                    trustScoreInputs={verificationTransactionDetailsData.verificationTransactionDetails.trustScoreInputs}
                                    trustScoreModifiers={verificationTransactionDetailsData.verificationTransactionDetails.trustScoreModifiers}
                                    assertionScoreBreakdowns={verificationTransactionDetailsData.verificationTransactionDetails.assertionScoreBreakdowns}
                                    assertionScoreModifiers={verificationTransactionDetailsData.verificationTransactionDetails.assertionScoreModifiers}
                                />
                            </ContentPageBody>
                        </CardContainer>
                        <CardContainer>
                            <ContentPageHeader title={resources.tables.verificationTransactionDetailsClaimsAssertions.title} headerButton={claimsTableHeaderButton} />
                            <ContentPageBody isMaxWidth={true}>
                                <ClaimAssertionsTableContainer claims={verificationTransactionDetailsData.verificationTransactionDetails.claims} />
                            </ContentPageBody>
                        </CardContainer>
                        <CardContainer>
                            <ContentPageHeader title={resources.tables.verificationTransactionDetailsTransactionEvents.title} headerButton={transactionEventsTableHeaderButton} />
                            <ContentPageBody isMaxWidth={true}>
                                <TransactionEventsTableContainer transactionEvents={verificationTransactionDetailsData.verificationTransactionDetails.transactionEvents} />
                            </ContentPageBody>
                        </CardContainer>
                    </>
                )}
            </ContentPageBody>
        </CardContainer>
    );
};